import axios from "axios";
import { demoRequestAdapter } from "../demo/demoInterceptor";

// In this Kubernetes-ingress setup, the React app and the FastAPI backend are
// always reachable on the same hostname. We always use a relative `/api` base
// so requests are same-origin and httpOnly auth cookies work without CORS.
// (REACT_APP_BACKEND_URL is intentionally ignored at runtime to avoid the
// cross-host CORS issue when the env is provisioned with a different
// preview hostname than the one the user is currently on.)
export const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Optional Authorization header support (in addition to httpOnly cookies)
let bearerToken = null;
export function setBearerToken(token) {
  bearerToken = token;
}

api.interceptors.request.use((config) => {
  if (bearerToken) {
    config.headers.Authorization = `Bearer ${bearerToken}`;
  }
  return config;
});

// Iter 20.08 — Demo-Adapter.
// Bei aktivem Demo-Modus (sessionStorage.et_demo_case gesetzt) fangen wir
// den Request VOR dem HTTP-Call ab und liefern pre-baked Responses. Der
// Adapter wird via axios' `adapter`-Option pro Request gesetzt.
api.interceptors.request.use((config) => {
  const canned = demoRequestAdapter(config);
  if (canned) {
    // Custom adapter: gibt die pre-baked Response direkt zurück.
    config.adapter = () => canned;
  }
  return config;
});

// ─── Audit-Fix S7 — automatischer Session-Refresh ───────────────────────────
// Der Access-Token läuft nach 12h ab, der Refresh-Cookie hält 7 Tage — aber
// bisher hat KEIN Frontend-Code /auth/refresh je aufgerufen: die Session
// endete hart nach 12h und ein über Nacht offener Wizard verlor beim
// nächsten Klick kommentarlos alle Eingaben. Jetzt: bei 401 einmalig
// /auth/refresh versuchen und den Original-Request wiederholen. Parallele
// 401s teilen sich EINEN Refresh-Call (refreshPromise-Mutex).
let refreshPromise = null;

function isAuthEndpoint(url) {
  const u = String(url || "");
  return (
    u.startsWith("/auth/login") ||
    u.startsWith("/auth/refresh") ||
    u.startsWith("/auth/logout") ||
    u.startsWith("/auth/register")
  );
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const config = error?.config;
    const status = error?.response?.status;
    if (status !== 401 || !config || config._retriedAfterRefresh || isAuthEndpoint(config.url)) {
      return Promise.reject(error);
    }
    try {
      if (!refreshPromise) {
        refreshPromise = api.post("/auth/refresh").finally(() => {
          refreshPromise = null;
        });
      }
      await refreshPromise;
    } catch {
      // Kein gültiger Refresh-Cookie → ursprüngliches 401 propagieren
      // (AuthContext behandelt den Logout-Zustand).
      return Promise.reject(error);
    }
    return api({ ...config, _retriedAfterRefresh: true });
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return "";
}

export function extractError(err, fallback = "Ein Fehler ist aufgetreten.", opts = {}) {
  const { aiContext = false } = opts;

  // Prefer the structured detail from the backend
  const fromDetail = formatApiErrorDetail(err?.response?.data?.detail);
  if (fromDetail) return fromDetail;

  // Network / timeout errors – axios sets err.code
  const code = err?.code;
  if (code === "ECONNABORTED" || code === "ETIMEDOUT") {
    return aiContext
      ? (
        "Die Anfrage hat zu lange gedauert (Timeout). Bei großen Dokumenten kann " +
        "die Auswertung länger dauern — bitte versuchen Sie es mit einem kleineren " +
        "Auszug oder erneut."
      )
      : "Die Anfrage hat zu lange gedauert. Bitte erneut versuchen.";
  }
  if (code === "ERR_NETWORK") {
    return "Verbindungsfehler. Bitte Internetverbindung prüfen und erneut versuchen.";
  }

  // Server returned a status without a JSON detail (e.g. ingress 502/504 HTML page)
  const status = err?.response?.status;
  if (status === 504 || status === 502 || status === 503) {
    return aiContext
      ? (
        "Der Server hat zu lange geantwortet. Bei sehr großen Dokumenten kann die " +
        "Auswertung das Zeitlimit überschreiten — bitte mit einem kürzeren Auszug erneut versuchen."
      )
      : "Der Server ist derzeit nicht erreichbar. Bitte in wenigen Minuten erneut versuchen.";
  }
  if (status === 413) {
    return "Die Datei ist zu groß. Bitte ein kleineres Dokument hochladen.";
  }
  if (status === 401) return "Sitzung abgelaufen. Bitte erneut anmelden.";
  if (status === 403) return "Keine Berechtigung für diese Aktion.";
  if (status === 404) return "Ressource nicht gefunden.";
  if (status === 409) return "Konflikt: Eintrag existiert bereits.";

  return err?.message || fallback;
}
